import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { Modal, Upload, message, Row, Col, Select, Typography, Button } from "antd";
const { Text } = Typography;
import { InboxOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { convertTemplate, downloadXlsx } from "../../templatesConverter";
import { avaliableBanks, OtherBankValue } from "../../utils";

const { Dragger } = Upload;

export const ConvertTemplateModal = forwardRef(({
	bankAccountSelected,		
	onOpenUploadTransaction,
	onFileContent,
	onClearFileList,
}, ref) => {
	const [OpenConvertTemplate, setOpenConvertTemplate] = useState(false);
	const [fileList, setFileList] = useState([]);
	const [selectedBank, setSelectedBank] = useState(bankAccountSelected?.bank || "");

	useImperativeHandle(ref, () => ({
		openModal: (value) => setOpenConvertTemplate(value),		
	}));
	
	useEffect(() => {
		if (onClearFileList) {
			onClearFileList(() => {
				setFileList([]);
				setSelectedBank("");
			});
		}
	}, [onClearFileList]);

	const getFileContent = async () => {
		const file = fileList[0].originFileObj; // Original file
		const reader = new FileReader();
	
		reader.onload = async (e) => {
			try {
				const data = new Uint8Array(e.target.result);
				const workbook = XLSX.read(data, { type: "array" });
	
				// Get the first sheet's data
				const firstSheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[firstSheetName];
				const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
	
				// Transform the data using convertTemplate
				const result = convertTemplate(jsonData, selectedBank);
	
				if (result && result.length > 1) {
					// Ensure result is a valid 2D array
					if (!Array.isArray(result) || !result.every(Array.isArray)) {
						throw new Error("Invalid data format: Result must be a 2D array.");
					}
	
					// Convert the transformed data back into a workbook
					const newWorkbook = XLSX.utils.book_new();
					const newWorksheet = XLSX.utils.aoa_to_sheet(result);
					XLSX.utils.book_append_sheet(newWorkbook, newWorksheet, "Processed");
	
					// Generate a Blob for the updated file
					const excelBlob = XLSX.write(newWorkbook, {
						bookType: "xlsx",
						type: "binary", // Use binary type first for compatibility
					});
	
					// Convert binary string to a Blob
					const buffer = new Uint8Array(excelBlob.length);
					for (let i = 0; i < excelBlob.length; i++) {
						buffer[i] = excelBlob.charCodeAt(i);
					}
					const finalBlob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

					// Create a new File object with the transformed data
					const updatedFile = new File(
						[finalBlob],
						`processed_${file.name}`,
						{ type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" } // Explicit MIME type
					);

					// Update fileList with the new file
					const updatedFileList = [
						{
							uid: file.uid,
							name: updatedFile.name,
							status: "done",
							originFileObj: updatedFile,
						},
					];
	
					// Pass the updated file and data to the parent
					setOpenConvertTemplate(false);
					onOpenUploadTransaction(true);
					onFileContent({
						file: updatedFileList, // Updated file list with the new file
						fileData: result,
						selectedBank,
					});
				} else {
					message.error("No se pudo procesar la plantilla.");
				}
			} catch (error) {
				console.error("Error al procesar el archivo:", error);
				message.error("Error al procesar el archivo.");
			} 
		};
	
		reader.onerror = (e) => {
			console.error("Error al leer el archivo:", e);
			message.error("Error al leer el archivo.");
		};
	
		reader.readAsArrayBuffer(file);
	};
	
	
	const nextStep = async () => {
		if (fileList.length === 0) {
			message.error("Por favor selecciona un archivo");
			return;
		}

		if(selectedBank.trim() === ""){
			message.error("Por favor selecciona un Banco");
			return;
		}

		await getFileContent();		
	};

	const handleChange = (info) => {
		let files = [...info.fileList];
		setFileList(files);
	};

	const handleChangeBank = (bank) => {
		setSelectedBank(bank);
	};

	return (
		<Modal
			open={OpenConvertTemplate}
			centered
			width='80vw'
			title='Convertir Plantilla'
			onCancel={() => {
				setOpenConvertTemplate(false);
				setFileList([]);
				setSelectedBank("");
			}}
			onOk={() => {
				nextStep();
			}}
			okText="Siguiente"
			cancelText="Cancelar"
		>
			<Row gutter={24}>
				<Col span={12}>
					<Dragger
						name="file"
						multiple={false}
						beforeUpload={() => false}
						onChange={handleChange}
						fileList={fileList}
						maxCount={1}
					>
						<p className="ant-upload-drag-icon"> <InboxOutlined /> </p>
						<p className="ant-upload-text">Sube tu plantilla</p>
						<p className="ant-upload-hint">Puedes subir una plantilla de tu banco de confianza</p>
					</Dragger>
				</Col>
				<Col span={12}>
					<Text>Banco:</Text>
					<br/>
					<br/>
					<Select
						style={{ width: "100%" }}
						options={[...bankAccountSelected? avaliableBanks.filter(bankAccount => bankAccount.value == bankAccountSelected.bank) : avaliableBanks, { label: "Otro Banco", value: OtherBankValue }]}
						onChange={handleChangeBank}
						value={selectedBank}
					/>
					
					{/*Set Information if the bank it doesnt in the way*/}
					{selectedBank === OtherBankValue && <>
						<br/>
						<br/>
						<Text>Porfavor revisa que tu plantilla contenga obligatoriamente estas columnas:</Text>
						<br/>
						<ul>
							<li><strong>Fecha</strong></li>
							<li><strong>Referencia / Documento / No. Doc</strong></li>
							<li><strong>Descripción</strong></li>
							<li><strong>Débito / Débitos</strong></li>
							<li><strong>Crédito / Créditos</strong></li>
							<li>Balance / Saldo Contable / Saldo Disponible</li>
						</ul>
						<Button 
							style={{ width: "100%" }}
							onClick={() => {
								const exampleExpectedColumns = [
									[
										"Fecha",
										"Referencia",
										"Descripción",
										"Débito",
										"Crédito",
										"Balance",
									],
									[
										"09/12/2024",
										"10101010",
										"Ejemplo 1",
										0,
										10,
										50,
									],
									[
										"12/12/2024",
										"20202020",
										"Ejemplo 2",
										15,
										0,
										75,
									],
								];
								downloadXlsx(exampleExpectedColumns, "example");
							}}
						>
							Descarga el Ejemplo de la plantilla aquí
						</Button>
					</>}
				</Col>				
			</Row>
		</Modal>
	);
});

ConvertTemplateModal.displayName = "ConvertTemplateModal";

ConvertTemplateModal.propTypes = {
	token: PropTypes.string.isRequired,
	bankAccountSelected: PropTypes.shape({
		bank: PropTypes.string.isRequired,
		account_number: PropTypes.string.isRequired,
		currency: PropTypes.string.isRequired,
		balance: PropTypes.number.isRequired,
		diaries: PropTypes.object.isRequired,
	}).isRequired,		
	onOpenUploadTransaction: PropTypes.func.isRequired,
	onFileContent: PropTypes.func.isRequired,
	onClearFileList: PropTypes.bool,
};
